import { useLazyQuery } from '@apollo/client';
import {
	CreateRedditAdvertiserMutationCreateAgencyAccount,
	RedditAdvertiserListDocument,
	RedditAdvertiserListQuery,
	RedditAdvertiserListQueryItems,
	UpdateRedditAdvertiserMutationUpdateAgencyAccount,
	UserAccountLevel,
} from 'wb-reddit/graphql/generated';
import {
	AuthGraphAdvertiserListDocument,
	AuthGraphAdvertiserListQuery,
	AuthGraphAdvertiserListQueryItems,
} from 'wb-spotify/graphql/authgraph/generated';
import {
	Button,
	FlagIcon,
	PlusIcon,
	GetRowsParams,
	InfiniteScrollList,
	isReddit,
	handleErrorNotification,
} from '@aleph/shared-components';
import { RedditHeaderSearch } from 'wb-reddit/app/common/components/headerSearch';
import RedditNotifications from 'wb-reddit/app/common/components/notifications';
import { useState, useCallback, useMemo } from 'react';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import AdvertiserListItem from 'wb-reddit/app/routes/advertisers/components/advertiserListItem';
import useRedditAdvertiserMutator, {
	newAdvertiser,
} from 'wb-reddit/app/routes/advertisers/hooks/useRedditAdvertiserMutator';
import RedditAdvertiserMutatorContainer from 'wb-reddit/app/routes/advertisers/mutator/container';
import AdvertisersListLoader from 'wb-reddit/app/routes/advertisers/components/loading';
import { userHasOneOfRoles } from 'wb-reddit/app/routes/utils';
import useRedditAdvertiserUrlOverlay from 'wb-reddit/app/routes/advertisers/hooks/useRedditAdvertiserUrlOverlay';
import AdvertiserOverlayLoader from 'wb-reddit/app/routes/advertisers/mutator/loader';
import { useLayoutRef } from 'wb-reddit/app/routes/context/layoutContext';
import useUpdateRows, { HandleUpdateRow } from 'wb-reddit/app/routes/hooks/useUpdateRows';
import { useTranslation } from 'react-i18next';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';
import { captureException } from '@sentry/react';

export type AdvertiserRow = UpdateRedditAdvertiserMutationUpdateAgencyAccount | CreateRedditAdvertiserMutationCreateAgencyAccount;

export type HandleUpdateAdvertiserRow = HandleUpdateRow<AdvertiserRow>;

const RedditAdvertisersContainer = () => {
	const platformUser = useUserMetaData(import.meta.env.VITE_PACKAGE_APP);
	const { t } = useTranslation();

	useRedditAdvertiserUrlOverlay();

	const {
		setMutatingAdvertiser,
		mutatingAdvertiser,
		loading: loadingMutatingAdvertiser,
	} = useRedditAdvertiserMutator();

	const [search, setSearch] = useState<string>('');

	const [getAdvertisers] = useLazyQuery(
		isReddit()
			? RedditAdvertiserListDocument
			: AuthGraphAdvertiserListDocument,
	);

	const getRows = useCallback(async (params: GetRowsParams<string | null>) => {
		const { data, error } = await getAdvertisers({
			variables: {
				cursor: params.metaData ?? undefined,
				search: search ? `*${search}*` : '**',
			},
			fetchPolicy: 'network-only',
		});

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					're: something went wrong while fetching advertisers',
					'Something went wrong while fetching advertisers',
				),
			);

			return {
				data:        [],
				hasNextPage: false,
				metaData:    null,
			};
		}

		if (isReddit()) {
			const redditData = data as unknown as RedditAdvertiserListQuery;

			return {
				data:        redditData?.agencyAccounts.items ?? [],
				hasNextPage: !!redditData?.agencyAccounts.cursor,
				metaData:    redditData?.agencyAccounts.cursor ?? null,
			};
		}

		const spotifyData = data as unknown as AuthGraphAdvertiserListQuery;

		return {
			data:        spotifyData?.advertiserAccounts.items ?? [],
			hasNextPage: !!spotifyData?.advertiserAccounts.cursor,
			metaData:    spotifyData?.advertiserAccounts.cursor ?? null,
		};
	}, [search]);

	const parentElement = useLayoutRef();

	// @ts-ignore
	const { exposedMethods, handleUpdateRow } = useUpdateRows<AdvertiserRow>();

	const { canCreateAdvertiserAccount: spotifyCanCreateAdvertiserAccount } = useSpotifyRolePermissions();

	const canCreateAdvertiserAccount = useMemo(() => {
		if (isReddit()) {
			return userHasOneOfRoles(platformUser, [UserAccountLevel.Agency, UserAccountLevel.Admin]);
		}

		return spotifyCanCreateAdvertiserAccount;
	}, [platformUser, spotifyCanCreateAdvertiserAccount]);

	if (loadingMutatingAdvertiser) {
		return (
			<div className="flex-1 flex flex-col px-[40px] gap-[16px] h-[100vh] w-full">
				<AdvertiserOverlayLoader />
			</div>
		);
	}

	return (
		<>
			{mutatingAdvertiser && !loadingMutatingAdvertiser && (
				<RedditAdvertiserMutatorContainer updateRow={handleUpdateRow} />
			)
			}
			<div className="flex flex-col gap-[16px] w-full sticky top-0 left-0 bg-gray-0 z-10">
				<div className="flex h-[64px] items-center justify-between border-b border-b-black"
					data-testid="advertisers-header-container"
				>
					<div className="flex-1 h-full flex items-center text-[18px] pr-[16px]
						text-gray-800 gap-[16px]"
					>
						<FlagIcon
							width={24}
							height={24}
							fill='hsl(var(--gray-800))'
						/>
						{t('sc: advertisers', 'Advertisers')}
					</div>

					<RedditHeaderSearch
						search={search}
						setSearch={setSearch}
					/>

					{isReddit() && (
						<div className="pl-[24px] h-full flex items-center">
							<RedditNotifications />
						</div>
					)}
				</div>

				{
					canCreateAdvertiserAccount && (
						<div>
							<Button
								title={t('sc: new', 'New')}
								leftNode={<PlusIcon />}
								onClick={() => setMutatingAdvertiser(newAdvertiser)}
							/>
						</div>
					)
				}

				<div
					className="flex  items-center mb-[-16px] whitespace-nowrap
						font-bold text-black border-b border-b-200 h-[36px] bg-gray-0"
				>
					{/* TODO translate */}
					<div className="px-[8px] flex-1">Advertiser</div>
					{isReddit() && <div className="px-[8px] w-[120px]">Status</div>}
					<div className="px-[8px] w-[202px]">Domain</div>
					<div className="px-[8px] w-[168px]">Access</div>
					<div className="px-[8px] w-[116px]">Accounts</div>
					{isReddit() && <div className="px-[8px] w-[90px]">Salesforce</div>}
					<div className="w-[56px]" />
				</div>
			</div>

			<InfiniteScrollList<string | null, RedditAdvertiserListQueryItems | AuthGraphAdvertiserListQueryItems>
				getRows={getRows}
				parentElement={parentElement?.current}
				// @ts-ignore
				exposedMethods={exposedMethods}
				renderItem={({ props, data, isLoaderRow }) => {
					if (isLoaderRow) {
						return <AdvertisersListLoader key={props.key} />;
					}

					return (<AdvertiserListItem
						{...props}
						advertiser={data}
						deleteRow={handleUpdateRow}
						data-testid={'advertiser-testtt'}
					/>);
				}}
			/>
		</>
	);
};

export default RedditAdvertisersContainer;
